import { isRejectedWithValue, Middleware } from '@reduxjs/toolkit'
import { toast } from 'react-toastify'
import i18n from '../../init/beforeAuth/language/i18n'
import { extractClientErrorCode } from '../../services/baseQueryWithErrorHandling'
import { LCToast } from '../../ui/components/lc-toast/Toast'
import { setClientErrorCode } from '../reducers/generalSlice'

/**
 * Middleware to handle 403 Forbidden errors generically
 * - If error has a clientErrorCode, dispatches it to Redux (existing system handles it)
 * - If error doesn't have a clientErrorCode, shows a generic permission error toast
 */
export const forbiddenMiddleware: Middleware =
  ({ dispatch }) =>
  (next) =>
  (action) => {
    // Redux middleware actions are typed as `unknown` in strict mode.
    // We cast once and validate what we access.
    const typedAction = action as any

    const hasHttpStatus = (payload: unknown): payload is { status: number } => {
      return (
        typeof payload === 'object' &&
        payload !== null &&
        'status' in payload &&
        typeof (payload as { status?: unknown }).status === 'number'
      )
    }

    // Check if it's a rejected action with 403 status
    // This middleware runs AFTER RTK Query middleware, so the action is already processed
    if (isRejectedWithValue(typedAction) && hasHttpStatus(typedAction.payload) && typedAction.payload.status === 403) {
      const error = typedAction.payload

      // Try to extract clientErrorCode from the error
      const clientErrorCode = extractClientErrorCode(error)

      if (clientErrorCode) {
        // If clientErrorCode exists, dispatch it - existing LCClientErrorCodeNotification will handle it
        dispatch(setClientErrorCode(clientErrorCode))
      } else {
        // No clientErrorCode — show Insufficient Permissions toast (ECOMM-3201)
        // Keys must exist in e-commander-common; do not use defaultValue fallbacks
        const title = i18n.t('common.insufficientPermissionsTitle', {
          ns: 'e-commander-common',
        })
        const body = i18n.t('common.insufficientPermissionsBody', {
          ns: 'e-commander-common',
        })

        toast.error(<LCToast title={title} body={body} />)
      }
    }

    return next(action)
  }
